import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { getOfflineAccountSnapshot } from '../../shell/session/offlineAccountCache';
import { useImpersonationStore } from '../support/useImpersonationStore';
import { isHiddenFromCommerceAuditEvent } from './auditVisibility';

const EXPENSE_AUDIT_EVENT_LABELS: Record<string, string> = {
  'expense.create': 'Gasto registrado',
  'expense.settlement': 'Pago de gasto registrado',
  'expense.settlement.void': 'Pago de gasto anulado',
  'expense.void': 'Gasto anulado',
};

const AUDIT_EVENT_LABELS: Record<string, string> = {
  'login.success': 'Inicio de sesión',
  'logout.manual': 'Cierre de sesión',
  'logout.password_recovery_dismissed': 'Sesión cerrada: recuperación de contraseña no completada',
  'logout.timeout': 'Sesión cerrada por inactividad',
  'support.impersonation.start': 'Soporte: inicio de impersonación',
  'support.impersonation.end': 'Soporte: fin de impersonación',
  'employee.permission.change': 'Cambio de permisos de empleado',
  'employee.revoked': 'Acceso de empleado revocado',
  'employee.branch.add': 'Sucursal agregada a empleado',
  'employee.branch.remove': 'Sucursal quitada a empleado',
  'invitation.sent': 'Invitación enviada',
  'invitation.email_failed': 'Fallo al enviar invitación',
  'invitation.accepted': 'Invitación aceptada',
  'catalog.product.update': 'Producto actualizado',
  'catalog.product.deactivate': 'Producto desactivado',
  'catalog.product.reactivate': 'Producto reactivado',
  'reports.cash.open': 'Caja abierta',
  'reports.cash.close': 'Caja cerrada',
  'reports.cash.outflow.create': 'Egreso de caja registrado',
  'reports.cash.outflow.void': 'Egreso de caja anulado',
  ...EXPENSE_AUDIT_EVENT_LABELS,
  'reports.external_service.create': 'Servicio externo registrado (Informes)',
  'pos.external_service.create': 'Recarga registrada (POS)',
  'pos.sale.create': 'Venta registrada (POS)',
  'pos.sale.void': 'Venta anulada (POS)',
  'stock.adjust': 'Ajuste de stock',
  'stock.receive': 'Ingreso de stock',
  'stock.transfer.send': 'Transferencia de stock enviada',
  'stock.transfer.receive': 'Transferencia de stock recibida',
  'stock.transfer.reject': 'Transferencia de stock rechazada',
  'stock.bulk_coefficient': 'Stock actualizado por porcentaje',
  'customer.charge': 'Cargo en cuenta corriente',
  'customer.payment': 'Pago de cuenta corriente',
  'customer.block': 'Bloqueo / desbloqueo de cliente',
  'customer.credit_limit': 'Cambio de límite de crédito',
  'customer.profile_create': 'Alta de cliente',
  'customer.profile_update': 'Edición de cliente',
  'catalog.category.create': 'Categoría creada',
  'catalog.category.update': 'Categoría actualizada',
  'catalog.category.delete': 'Categoría eliminada',
  'catalog.category.restore': 'Categoría restaurada',
  'catalog.brand.create': 'Marca creada',
  'catalog.brand.update': 'Marca actualizada',
  'catalog.brand.delete': 'Marca eliminada',
  'catalog.brand.restore': 'Marca restaurada',
  'catalog.supplier.create': 'Proveedor creado',
  'catalog.supplier.update': 'Proveedor actualizado',
  'catalog.supplier.delete': 'Proveedor eliminado',
  'catalog.supplier.restore': 'Proveedor restaurado',
  'branch.settings.identity': 'Identidad de sucursal actualizada',
  'branch.settings.stock_policy': 'Política de venta con stock actualizada',
  'branch.settings.barcode_input': 'Modo de lectura de códigos de barras actualizado',
  'branch.settings.mp_pos': 'Configuración POS QR (Mercado Pago)',
  'branch.settings.mp_secrets': 'Secrets Mercado Pago actualizados',
  'branch.settings.mp_store': 'Tienda Mercado Pago actualizada',
  'branch.settings.mp_store_delete': 'Tienda Mercado Pago eliminada',
  'branch.settings.manual_discount_threshold': 'Umbrales de descuento manual actualizados',
  'promotions.promotion.create': 'Promoción creada',
  'promotions.promotion.update': 'Promoción actualizada',
  'promotions.promotion.activate': 'Promoción activada',
  'promotions.promotion.deactivate': 'Promoción desactivada',
  'promotions.promotion.delete': 'Promoción eliminada',
  'promotions.promotion.restore': 'Promoción restaurada',
  'pos.promotion.apply_manual': 'Descuento manual aplicado en POS',
  'purchases.create': 'Orden de compra registrada',
  'purchases.send': 'Orden de compra enviada',
  'purchases.void': 'Orden de compra anulada',
  'purchases.close': 'Orden de compra cerrada',
  'purchases.receive': 'Recepción de compra registrada',
  'purchases.receive_void': 'Recepción de compra anulada',
  'purchases.return': 'Devolución a proveedor registrada',
  'purchases.return_void': 'Devolución a proveedor anulada',
};

/** Tabla en schema `audit` (post-migración dominios). */
const auditTable = () => supabase.schema('audit').from('audit_logs');

export type AuditEventType =
  | 'login.success'
  | 'logout.manual'
  | 'logout.password_recovery_dismissed'
  | 'logout.timeout'
  | 'employee.permission.change'
  | 'catalog.product.update'
  | 'catalog.product.deactivate'
  | 'catalog.product.reactivate'
  | 'reports.cash.open'
  | 'reports.cash.close'
  | 'reports.cash.outflow.create'
  | 'reports.cash.outflow.void'
  | 'expense.create'
  | 'expense.void'
  | 'expense.settlement'
  | 'expense.settlement.void'
  | 'reports.external_service.create'
  | 'pos.external_service.create'
  | 'pos.sale.create'
  | 'pos.sale.void'
  | 'stock.adjust'
  | 'stock.receive'
  | 'stock.transfer.send'
  | 'stock.transfer.receive'
  | 'stock.transfer.reject'
  | 'stock.bulk_coefficient'
  | 'customer.charge'
  | 'customer.payment'
  | 'customer.block'
  | 'customer.credit_limit'
  | 'customer.profile_create'
  | 'customer.profile_update'
  | 'catalog.category.create'
  | 'catalog.category.update'
  | 'catalog.category.delete'
  | 'catalog.category.restore'
  | 'catalog.brand.create'
  | 'catalog.brand.update'
  | 'catalog.brand.delete'
  | 'catalog.brand.restore'
  | 'catalog.supplier.create'
  | 'catalog.supplier.update'
  | 'catalog.supplier.delete'
  | 'catalog.supplier.restore'
  | 'branch.settings.identity'
  | 'branch.settings.stock_policy'
  | 'branch.settings.barcode_input'
  | 'branch.settings.mp_pos'
  | 'branch.settings.mp_secrets'
  | 'branch.settings.mp_store'
  | 'branch.settings.mp_store_delete'
  | 'branch.settings.manual_discount_threshold'
  | 'promotions.promotion.create'
  | 'promotions.promotion.update'
  | 'promotions.promotion.activate'
  | 'promotions.promotion.deactivate'
  | 'promotions.promotion.delete'
  | 'promotions.promotion.restore'
  | 'pos.promotion.apply_manual';

export type AuditLogRow = {
  id: string;
  user_id: string | null;
  event_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

const PAGE_SIZE = 40;

function applyCommerceAuditVisibility<Q extends { not: (column: string, op: string, value: string) => Q }>(
  query: Q,
): Q {
  return query.not('event_type', 'like', 'support.impersonation%');
}

export const logAuditEvent = async (eventType: AuditEventType | string, metadata?: Record<string, unknown>) => {
  const { user } = useAuthStore.getState();
  if (!user) return;
  if (useImpersonationStore.getState().context != null) return;
  if (isHiddenFromCommerceAuditEvent(eventType)) return;

  const merged: Record<string, unknown> = { ...(metadata ?? {}) };

  try {
    const { error } = await auditTable().insert({
      user_id: user.id,
      event_type: eventType,
      metadata: merged,
    });
    if (error) throw error;
  } catch (error) {
    console.warn(`[audit_logs] No se pudo registrar el evento ${eventType}`, error);
  }
};

/** Eventos de negocio (permisos, catálogo). Respeta `audit_enabled` del plan cuando hay caché; si no hay datos, se registra. */
export const logBusinessAuditEvent = async (
  eventType: string,
  metadata?: Record<string, unknown>
): Promise<void> => {
  const { user } = useAuthStore.getState();
  if (!user) return;
  if (useImpersonationStore.getState().context != null) return;
  if (isHiddenFromCommerceAuditEvent(eventType)) return;
  let enabled = true;
  let organizationId: string | undefined;
  try {
    const snap = await getOfflineAccountSnapshot(user.id);
    enabled = snap?.entitlements?.audit_enabled !== false;
    const oid = snap?.entitlements?.organization_id?.trim();
    if (oid) organizationId = oid;
  } catch {
    enabled = true;
  }
  if (!enabled) return;

  const merged: Record<string, unknown> = { ...(metadata ?? {}) };
  if (organizationId && merged.organization_id == null) {
    merged.organization_id = organizationId;
  }

  try {
    const { error } = await auditTable().insert({
      user_id: user.id,
      event_type: eventType,
      metadata: merged,
    });
    if (error) throw error;
  } catch (error) {
    console.warn(`[audit_logs] business ${eventType}`, error);
  }
};

export type ListAuditLogsPageOptions = {
  /**
   * Si true, solo filas con `user_id` = usuario actual.
   * Empleados (no dueños): usar true para alinear la consulta con la política RLS
   * (`user_id = auth.uid()`; no leen eventos de org por metadata).
   * Dueños: false para permitir también filas de la org (misma política RLS).
   */
  onlyCurrentUser?: boolean;
};

/**
 * Lista eventos de auditoría visibles para el usuario (orden: más recientes primero).
 * RLS en `audit.audit_logs`: el dueño puede ver sus eventos y los de la org vía `metadata.organization_id`;
 * el resto solo ve `user_id = auth.uid()`.
 */
export async function listAuditLogsPage(offset: number, options?: ListAuditLogsPageOptions, limit: number = PAGE_SIZE): Promise<AuditLogRow[]> {
  const { user } = useAuthStore.getState();
  let query = applyCommerceAuditVisibility(
    auditTable()
      .select('id, user_id, event_type, metadata, created_at')
      .order('created_at', { ascending: false }),
  );

  if (options?.onlyCurrentUser === true && user?.id) {
    query = query.eq('user_id', user.id);
  }

  const { data, error } = await query.range(offset, offset + limit - 1);

  if (error) {
    console.warn('[audit_logs] listAuditLogsPage', error.message);
    return [];
  }
  return (data ?? []) as AuditLogRow[];
}

export type TeamAuditLogsRange = {
  /** Inclusive start (ISO datetime). */
  fromIso?: string;
  /** Inclusive end (ISO datetime). */
  toIso?: string;
  /** Filter by user_id (server-side). */
  userId?: string | null;
  /** Filter by category (server-side via event_type prefixes). */
  categoryFilter?: string | null;
};

/** Eventos del equipo (metadata.organization_id = org del dueño). Requiere RLS de lectura por org. */
export async function listTeamAuditLogsPage(
  offset: number,
  organizationId: string,
  limit: number = PAGE_SIZE,
  range?: TeamAuditLogsRange,
): Promise<AuditLogRow[]> {
  if (!organizationId.trim()) return [];
  let query = applyCommerceAuditVisibility(
    auditTable()
      .select('id, user_id, event_type, metadata, created_at')
      .contains('metadata', { organization_id: organizationId })
      .order('created_at', { ascending: false }),
  );

  if (range?.fromIso) query = query.gte('created_at', range.fromIso);
  if (range?.toIso) query = query.lte('created_at', range.toIso);
  if (range?.userId) query = query.eq('user_id', range.userId);
  if (range?.categoryFilter) query = query.or(range.categoryFilter);

  const { data, error } = await query.range(offset, offset + limit - 1);

  if (error) {
    console.warn('[audit_logs] listTeamAuditLogsPage', error.message);
    return [];
  }
  return (data ?? []) as AuditLogRow[];
}

export function getAuditLogsPageSize(): number {
  return PAGE_SIZE;
}

/** Cuenta total de eventos visibles para el usuario (para paginación). */
export async function countAuditLogs(options?: ListAuditLogsPageOptions): Promise<number> {
  const { user } = useAuthStore.getState();
  let query = applyCommerceAuditVisibility(
    auditTable()
      .select('*', { count: 'exact', head: true }),
  );

  if (options?.onlyCurrentUser === true && user?.id) {
    query = query.eq('user_id', user.id);
  }

  const { count, error } = await query;
  if (error) {
    console.warn('[audit_logs] countAuditLogs', error.message);
    return 0;
  }
  return count ?? 0;
}

/** Cuenta total de eventos del equipo para paginación. */
export async function countTeamAuditLogs(
  organizationId: string,
  range?: TeamAuditLogsRange,
): Promise<number> {
  if (!organizationId.trim()) return 0;
  let query = applyCommerceAuditVisibility(
    auditTable()
      .select('*', { count: 'exact', head: true })
      .contains('metadata', { organization_id: organizationId }),
  );

  if (range?.fromIso) query = query.gte('created_at', range.fromIso);
  if (range?.toIso) query = query.lte('created_at', range.toIso);
  if (range?.userId) query = query.eq('user_id', range.userId);
  if (range?.categoryFilter) query = query.or(range.categoryFilter);

  const { count, error } = await query;
  if (error) {
    console.warn('[audit_logs] countTeamAuditLogs', error.message);
    return 0;
  }
  return count ?? 0;
}

/** Etiqueta breve en español para mostrar en UI. */
export function formatAuditEventLabel(eventType: string): string {
  return AUDIT_EVENT_LABELS[eventType] ?? eventType;
}
